import { useCallback, useEffect, useMemo, useState } from 'react';
import { refundTransaction, transactions, type Transaction } from './api';
import type { PaymentsKey, PaymentsTranslate } from './i18n';
import {
  Money,
  MoneyInput,
  StatusBadge,
  Table,
  type BadgeTone,
  Field,
  Pagination,
  Reveal,
  TableSkeleton,
  usePagination,
} from '../../shell/kit';
import { CustomerPicker } from '../../shell/pickers';

// Тон берётся из общей шкалы кита, а не из палитры Tailwind напрямую: цвета
// вроде `bg-sky-100` не знают про тёмную тему и остались бы светлыми пятнами.
const STATUS_TONE: Record<Transaction['status'], BadgeTone> = {
  pending: 'neutral',
  authorized: 'brand',
  captured: 'ok',
  failed: 'danger',
  refunded: 'warn',
};

// Способ заполнен только у provider = pos (эквайринг платит всегда картой —
// там его нет и не нужно). Для способа без перевода показываем исходное
// значение вместо ключа словаря: неизвестный способ понятнее сырой строки
// «tx.method.bonus», а не наоборот.
function methodLabel(t: PaymentsTranslate, method: string | null): string {
  if (!method) return '—';
  const key = `tx.method.${method}` as PaymentsKey;
  const label = t(key);
  return label === key ? method : label;
}

export interface TransactionsTabProps {
  readonly t: PaymentsTranslate;
  readonly onError: (message: string) => void;
}

export function TransactionsTab({ t, onError }: TransactionsTabProps) {
  const [rows, setRows] = useState<readonly Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Фильтр по клиенту, а не строка «заказ или клиент». Прежнее одно поле
  // отправляло введённое значение сразу и как `order_id`, и как `customer_id`,
  // а сервер их складывает через AND: `order_id = X AND customer_id = X`
  // невыполнимо ни при каком X, и фильтр не мог найти ничего в принципе.
  // Заметить это было нечем — поле просило ULID, и валидного значения для него
  // взять было неоткуда.
  const [customerId, setCustomerId] = useState('');
  const [refunding, setRefunding] = useState<string | undefined>(undefined);
  // Сумма возврата хранится в тиынах, вводится в тенге: поле принимало сырые
  // минорные единицы, и «5000» означало пятьдесят тенге, а не пять тысяч.
  const [amount, setAmount] = useState<number | null>(null);
  const [done, setDone] = useState(false);

  const reload = useCallback(
    async (value = ''): Promise<void> => {
      setLoading(true);
      try {
        const page = await transactions(value.trim() ? { customer: value.trim() } : {});
        setRows(page.items);
      } catch (err) {
        onError((err as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [onError],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  function submitRefund(id: string): void {
    setBusy(true);
    setDone(false);
    const value = amount ?? undefined;
    refundTransaction(id, value)
      .then(async () => {
        setDone(true);
        setRefunding(undefined);
        setAmount(null);
        await reload(customerId);
      })
      .catch((err: Error) => onError(err.message))
      .finally(() => setBusy(false));
  }

  // Возврат — отдельная строка со ссылкой на исходную операцию (parentId), а
  // не признак на ней самой. Без этой карты частичный возврат было не увидеть
  // нигде: исходная операция как ни в чём не бывало показывала «Проведён» на
  // всю сумму, а строка возврата стояла в списке сама по себе.
  const refundedByParent = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) {
      if (row.kind !== 'refund' || !row.parentId) continue;
      map.set(row.parentId, (map.get(row.parentId) ?? 0) + row.amountMinor);
    }
    return map;
  }, [rows]);

  // Операций за период набирается под сотню — одним полотном их не читают.
  const paged = usePagination(rows);

  /*
   * Скелет рисуется НА МЕСТЕ таблицы, а не вместо всей вкладки. Ранний возврат
   * уносил вместе с таблицей и фильтр, и заголовок: при каждой перезагрузке
   * список приходилось искать заново, а страница подпрыгивала.
   */
  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h3 className="text-lg font-semibold text-ink">{t('tx.title')}</h3>
        <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto">
          <Field
            label={t('tx.filter')}
            hint={t('tx.filterHint')}
            className="min-w-0 flex-1 sm:w-72 sm:flex-none"
          >
            <CustomerPicker
              value={customerId}
              onChange={(id) => {
                setCustomerId(id);
                void reload(id);
              }}
            />
          </Field>
        </div>
      </header>

      {done && <p className="text-sm text-ok">{t('tx.refundDone')}</p>}

      {/*
        * Форма возврата стоит НАД таблицей и прокручивает себя в вид.
        * Раньше она рисовалась после таблицы: на списке в полсотни строк
        * нажатие «Возврат» не меняло ничего видимого, и человек жал ещё раз.
        */}
      {refunding && (
        <Reveal when={refunding}>
          <div className="flex flex-wrap items-end gap-3 rounded-card border border-brand bg-surface p-4">
            <label className="text-xs text-ink-muted">
              <span className="mb-1 block">{t('tx.refundAmount')}</span>
              <MoneyInput
                className="w-36"
                placeholder={t('tx.refundAll')}
                valueMinor={amount}
                onChangeMinor={setAmount}
              />
            </label>
            <button
              type="button"
              disabled={busy}
              className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
              onClick={() => submitRefund(refunding)}
            >
              {t('tx.refund')}
            </button>
            <button
              type="button"
              className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
              onClick={() => setRefunding(undefined)}
            >
              {t('common.cancel')}
            </button>
          </div>
        </Reveal>
      )}


      {loading ? (
        <TableSkeleton rows={6} columns={6} />
      ) : rows.length === 0 ? (
        <p className="text-ink-muted">{t('tx.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-line bg-surface">
          <Table className="text-left">
            <thead className="bg-sunken text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-3 py-2">{t('tx.date')}</th>
                <th className="px-3 py-2">{t('tx.provider')}</th>
                <th className="px-3 py-2">{t('tx.method')}</th>
                <th className="px-3 py-2">{t('tx.kind')}</th>
                <th className="px-3 py-2">{t('tx.amount')}</th>
                <th className="px-3 py-2">{t('tx.status')}</th>
                <th className="px-3 py-2">{t('tx.external')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {paged.rows.map((row) => {
                const refundedMinor = refundedByParent.get(row.id) ?? 0;
                // Полный возврат уже виден через статус «Возвращён» — бейдж
                // здесь нужен ровно для случая, когда статус молчит: часть
                // денег вернули, а строка как ни в чём не бывало показывает
                // «Проведён» на всю сумму.
                const isPartialRefund = refundedMinor > 0 && refundedMinor < row.amountMinor;
                return (
                  <tr
                    key={row.id}
                    className={
                      row.kind === 'refund'
                        ? 'border-t border-line border-l-2 border-l-warn bg-warn-bg align-top'
                        : 'border-t border-line align-top'
                    }
                  >
                    <td className="px-3 py-2 text-xs">{new Date(row.createdAt).toLocaleString()}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.provider}</td>
                    <td className="px-3 py-2 text-xs">{methodLabel(t, row.method)}</td>
                    <td className="px-3 py-2">{t(`tx.kind.${row.kind}` as PaymentsKey)}</td>
                    <td className="px-3 py-2 font-medium">
                      <Money minor={row.amountMinor} />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={STATUS_TONE[row.status]}>
                        {t(`tx.status.${row.status}` as PaymentsKey)}
                      </StatusBadge>
                      {isPartialRefund && (
                        <div className="mt-1">
                          <StatusBadge tone="warn">
                            {t('tx.partialRefund')}: <Money minor={refundedMinor} />
                          </StatusBadge>
                        </div>
                      )}
                      {row.failureReason && (
                        <div className="mt-1 text-xs text-danger">
                          {t(`reason.${row.failureReason}` as PaymentsKey)}
                          {row.failureCode ? ` · ${row.failureCode}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{row.externalId ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {row.kind !== 'refund' && row.status === 'captured' && (
                        <button
                          type="button"
                          className="text-xs text-ink-muted underline"
                          onClick={() => {
                            setRefunding(row.id);
                            setAmount(null);
                          }}
                        >
                          {t('tx.refund')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <div className="px-3 pb-3">
            <Pagination paged={paged} />
          </div>
        </div>
      )}

    </section>
  );
}
